import { getApiUrl } from '@/lib/api-config';

export interface Coordinates {
  latitude: number
  longitude: number
}

export interface WeatherResult {
  currentTempC: number
  dailyHighC: number
  dailyLowC: number
  weatherCode: number
  conditionText: string
  windKph?: number
  humidityPercent?: number
  precipitationMm?: number
  raw: any
}

const weatherCodeToCondition: Record<number, string> = {
  0: "Clear",
  1: "Mostly clear",
  2: "Partly cloudy",
  3: "Overcast",
  45: "Fog",
  48: "Freezing fog",
  51: "Light drizzle",
  53: "Drizzle",
  55: "Heavy drizzle",
  56: "Light freezing drizzle",
  57: "Freezing drizzle",
  61: "Light rain",
  63: "Rain",
  65: "Heavy rain",
  66: "Light freezing rain",
  67: "Freezing rain",
  71: "Light snow",
  73: "Snow",
  75: "Heavy snow",
  77: "Snow grains",
  80: "Light showers",
  81: "Showers",
  82: "Heavy showers",
  85: "Light snow showers",
  86: "Heavy snow showers",
  95: "Thunderstorm",
  96: "Thunderstorm (hail)",
  99: "Severe thunderstorm (hail)",
}

const weatherCodeToEmoji: Record<number, string> = {
  0: "☀️",
  1: "🌤️",
  2: "⛅",
  3: "☁️",
  45: "🌫️",
  48: "🌫️",
  51: "🌦️",
  53: "🌦️",
  55: "🌧️",
  56: "🌧️",
  57: "🌧️",
  61: "🌦️",
  63: "🌧️",
  65: "🌧️",
  66: "🌨️",
  67: "🌨️",
  71: "🌨️",
  73: "🌨️",
  75: "❄️",
  77: "❄️",
  80: "🌧️",
  81: "🌧️",
  82: "⛈️",
  85: "🌨️",
  86: "🌨️",
  95: "⛈️",
  96: "⛈️",
  99: "⛈️",
}

const DEFAULT_WEATHER_EMOJI = "🌤️"

function mapWeatherCodeToCondition(code: number): string {
  return weatherCodeToCondition[code] ?? "Unknown"
}

function mapWeatherCodeToEmoji(code: number): string {
  return weatherCodeToEmoji[code] ?? DEFAULT_WEATHER_EMOJI
}

export interface DailyWeatherSummary {
  date: string
  highC: number
  lowC: number
  weatherCode: number
  conditionText: string
  emoji: string
}

export async function fetchWeatherForLocation(coords: Coordinates): Promise<WeatherResult> {
  const url = new URL(getApiUrl('api/weather'))
  url.searchParams.set("latitude", String(coords.latitude))
  url.searchParams.set("longitude", String(coords.longitude))

  try {
    console.log("🌦️ Fetching weather from:", url.toString())

    const response = await fetch(url.toString())

    console.log("HTTP status:", response.status)
    if (!response.ok) throw new Error(`Weather API error: ${response.status}`)

    const data = await response.json()
    console.log("✅ Weather data received:", data)

    const currentTempC: number = data?.current_weather?.temperature ?? NaN
    const weatherCode: number = data?.current_weather?.weathercode ?? data?.daily?.weathercode?.[0] ?? -1
    const dailyHighC: number = data?.daily?.temperature_2m_max?.[0] ?? currentTempC
    const dailyLowC: number = data?.daily?.temperature_2m_min?.[0] ?? currentTempC
    const conditionText = mapWeatherCodeToCondition(weatherCode)
    const windKph: number | undefined = data?.current_weather?.windspeed

    let humidityPercent: number | undefined
    let precipitationMm: number | undefined

    try {
      const hourlyTimes: string[] | undefined = data?.hourly?.time
      const humidityArr: number[] | undefined = data?.hourly?.relativehumidity_2m
      const precipArr: number[] | undefined = data?.hourly?.precipitation
      if (hourlyTimes && hourlyTimes.length && humidityArr && precipArr) {
        const nowIsoHour = new Date().toISOString().slice(0, 13)
        const idx = hourlyTimes.findIndex((t) => t.startsWith(nowIsoHour))
        if (idx >= 0) {
          humidityPercent = typeof humidityArr[idx] === "number" ? humidityArr[idx] : undefined
          precipitationMm = typeof precipArr[idx] === "number" ? precipArr[idx] : undefined
        }
      }
    } catch (innerError) {
      console.warn("⚠️ Error extracting hourly data:", innerError)
    }

    return {
      currentTempC,
      dailyHighC,
      dailyLowC,
      weatherCode,
      conditionText,
      windKph,
      humidityPercent,
      precipitationMm,
      raw: data,
    }
  } catch (error) {
    console.error("❌ fetchWeatherForLocation failed:", error)
    throw error
  }
}


export async function fetchDailyWeatherForRange(
  coords: Coordinates,
  startDate: Date,
  endDate: Date
): Promise<DailyWeatherSummary[]> {
  const start = startDate.toISOString().split("T")[0]
  const end = endDate.toISOString().split("T")[0]

  const url = new URL(getApiUrl('api/weather/range'))
  url.searchParams.set("latitude", String(coords.latitude))
  url.searchParams.set("longitude", String(coords.longitude))
  url.searchParams.set("start_date", start)
  url.searchParams.set("end_date", end)

  const response = await fetch(url.toString())
  if (!response.ok) {
    throw new Error(`Weather API error: ${response.status}`)
  }

  const data = await response.json()
  const times: string[] = Array.isArray(data?.daily?.time) ? data.daily.time : []
  const highs: number[] = Array.isArray(data?.daily?.temperature_2m_max) ? data.daily.temperature_2m_max : []
  const lows: number[] = Array.isArray(data?.daily?.temperature_2m_min) ? data.daily.temperature_2m_min : []
  const codes: number[] = Array.isArray(data?.daily?.weathercode) ? data.daily.weathercode : []

  if (!times.length || times.length !== highs.length || times.length !== lows.length || times.length !== codes.length) {
    console.warn("Weather API returned unexpected daily forecast payload", { times: times.length, highs: highs.length, lows: lows.length, codes: codes.length })
    return []
  }

  return times.map((date, index) => {
    const weatherCode = typeof codes[index] === "number" ? codes[index] : -1
    const highC = typeof highs[index] === "number" ? highs[index] : NaN
    const lowC = typeof lows[index] === "number" ? lows[index] : NaN

    return {
      date,
      highC,
      lowC,
      weatherCode,
      conditionText: mapWeatherCodeToCondition(weatherCode),
      emoji: mapWeatherCodeToEmoji(weatherCode),
    }
  })
}

export function formatWeatherText(result: WeatherResult): string {
  const high = Math.round(result.dailyHighC)
  const low = Math.round(result.dailyLowC)
  return `${result.conditionText} • High ${high}°C / Low ${low}°C`
}

export async function geocodeAddressToCoords(fullAddress: string): Promise<Coordinates> {
  const url = new URL(getApiUrl('api/geocode'))
  url.searchParams.set("q", fullAddress)

  const response = await fetch(url.toString())
  if (!response.ok) {
    throw new Error(`Geocoding error: ${response.status}`)
  }
  const results = await response.json()
  if (!Array.isArray(results) || results.length === 0) {
    throw new Error("Address not found")
  }
  const first = results[0]
  return { latitude: parseFloat(first.lat), longitude: parseFloat(first.lon) }
}

export function buildFullAddress(parts: Array<string | undefined | null>): string {
  return parts.filter(Boolean).join(", ")
}


